import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  Slider,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import api from "../../utils/api";
import {
  loadingAlert,
  closeAlert,
  successAlert,
  errorAlert,
} from "../../utils/swalConfig";

const RewardSettings = () => {
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    dailyRewardAmount: 100,
    availableBalancePercentage: 50,
    loginReward: {
      enabled: true,
      amount: 500,
    },
  });

  // Remove loading and saving states since we're using alerts
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      loadingAlert("Loading reward settings...");
      const token = localStorage.getItem("jwt");
      const response = await api.get("/rewards/settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data.data.settings);
      setError(null);
      closeAlert();
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error!",
        err.response?.data?.message || "Failed to fetch reward settings"
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      loadingAlert("Updating reward settings...");
      const token = localStorage.getItem("jwt");
      await api.patch(
        "/rewards/settings",
        {
          dailyRewardAmount: Number(settings.dailyRewardAmount),
          availableBalancePercentage: Number(
            settings.availableBalancePercentage
          ),
          loginReward: {
            enabled: settings.loginReward.enabled,
            amount: Number(settings.loginReward.amount),
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      closeAlert();
      successAlert(
        "Settings Updated",
        "Reward settings have been successfully updated!"
      );
    } catch (err) {
      closeAlert();
      errorAlert(
        "Update Failed",
        err.response?.data?.message || "Failed to update reward settings"
      );
    }
  };

  const handleSliderChange = (event, newValue) => {
    setSettings((prev) => ({
      ...prev,
      availableBalancePercentage: newValue,
    }));
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={3}>
          <SettingsIcon sx={{ color: "primary.main", mr: 1 }} />
          <Typography variant="h6">Reward Settings</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Daily Reward Section */}
          <Typography variant="h6" gutterBottom>
            Daily Reward Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Daily Reward Amount"
                type="number"
                value={settings.dailyRewardAmount}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    dailyRewardAmount: e.target.value,
                  })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₦</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Distribution Percentage
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Adjust how the reward amount is split between Available Balance
                and Bonus Wallet
              </Typography>
              <Box px={2} mt={2}>
                <Slider
                  value={settings.availableBalancePercentage}
                  onChange={handleSliderChange}
                  valueLabelDisplay="on"
                  step={5}
                  marks
                  min={0}
                  max={100}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ bgcolor: "action.hover", p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Preview Distribution for ₦{settings.dailyRewardAmount}:
                </Typography>
                <Typography variant="body2">
                  • Available Balance: ₦
                  {(
                    (settings.dailyRewardAmount *
                      settings.availableBalancePercentage) /
                    100
                  ).toFixed(2)}
                  ({settings.availableBalancePercentage}%)
                </Typography>
                <Typography variant="body2">
                  • Bonus Wallet: ₦
                  {(
                    (settings.dailyRewardAmount *
                      (100 - settings.availableBalancePercentage)) /
                    100
                  ).toFixed(2)}
                  ({100 - settings.availableBalancePercentage}%)
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Login Reward Section */}
          <Typography variant="h6" gutterBottom>
            Login Reward Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.loginReward.enabled}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        loginReward: {
                          ...prev.loginReward,
                          enabled: e.target.checked,
                        },
                      }))
                    }
                  />
                }
                label="Enable Daily Login Rewards"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Login Reward Amount"
                type="number"
                disabled={!settings.loginReward.enabled}
                value={settings.loginReward.amount}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    loginReward: {
                      ...prev.loginReward,
                      amount: Number(e.target.value),
                    },
                  }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₦</InputAdornment>
                  ),
                }}
              />
              <Typography variant="caption" color="text.secondary">
                This amount will be added to user's bonus wallet upon daily
                login
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Update Button - Moved to bottom */}
          <Box sx={{ mt: 3 }}>
            <Button type="submit" variant="contained" fullWidth size="large">
              Update Reward Settings
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default RewardSettings;
